// PWA Install Utility
// Handles Progressive Web App installation prompts for iOS and Android
// Provides custom install UI

let deferredPrompt = null;
let installListeners = [];

// Listen for the install prompt event (Android)
if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        // Prevent the mini-infobar from appearing on mobile
        e.preventDefault();
        
        // Stash the event so it can be triggered later
        deferredPrompt = e;
        
        // Notify listeners that install is available
        installListeners.forEach(listener => listener(true));
    });

    // Track if app was installed
    window.addEventListener('appinstalled', () => {
        console.log('PWA installed successfully');
        deferredPrompt = null;
        
        // Track installation (analytics)
        if (window.gtag) {
            window.gtag('event', 'pwa_install', {
                event_category: 'engagement',
                event_label: 'PWA Installed'
            });
        }
    });
}

/**
 * Check if PWA install is available (Android)
 * @returns {boolean}
 */
export const isInstallAvailable = () => {
    return deferredPrompt !== null;
};

/**
 * Check if running as installed PWA
 * @returns {boolean}
 */
export const isInstalled = () => {
    // Check if running in standalone mode (installed PWA)
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone || // iOS
           document.referrer.includes('android-app://');
};

/**
 * Check if device is iOS
 * @returns {boolean}
 */
export const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * Check if device is Android
 * @returns {boolean}
 */
export const isAndroid = () => {
    return /Android/.test(navigator.userAgent);
};

/**
 * Prompt user to install PWA (Android)
 * @returns {Promise<boolean>} True if user accepted, false if declined
 */
export const promptInstall = async () => {
    if (!deferredPrompt) {
        console.log('Install prompt not available');
        return false;
    }
    
    try {
        // Show the install prompt
        deferredPrompt.prompt();
        
        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;
        
        console.log(`User response to install prompt: ${outcome}`);
        
        // Clear the saved prompt
        deferredPrompt = null;
        
        // Notify listeners
        installListeners.forEach(listener => listener(false));
        
        return outcome === 'accepted';
    } catch (error) {
        console.error('Error showing install prompt:', error);
        return false;
    }
};

/**
 * Subscribe to install availability changes
 * @param {function} callback - Called with boolean indicating if install is available
 * @returns {function} Unsubscribe function
 */
export const onInstallAvailabilityChange = (callback) => {
    installListeners.push(callback);
    
    // Immediately call with current state
    callback(isInstallAvailable());
    
    // Return unsubscribe function
    return () => {
        installListeners = installListeners.filter(l => l !== callback);
    };
};

/**
 * Get iOS install instructions
 * @returns {string[]} Array of instruction steps
 */
export const getIOSInstructions = () => {
    return [
        'Tap the Share button in Safari',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right corner',
        'The app will appear on your home screen'
    ];
};

/**
 * Show iOS install instructions modal
 * Can be used to display instructions to iOS users
 */
export const showIOSInstructions = () => {
    return {
        title: 'Install SuperCalc Pro',
        instructions: getIOSInstructions(),
        icon: '📱'
    };
};

export default {
    isInstallAvailable,
    isInstalled,
    isIOS,
    isAndroid,
    promptInstall,
    onInstallAvailabilityChange,
    getIOSInstructions,
    showIOSInstructions
};

